import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { LayoutDashboard, Loader2, Warehouse, Layers, Truck, MapPin, AlertTriangle, ArrowUpRight, RefreshCw } from "lucide-react";

const pad = (n) => String(n).padStart(2, "0");
const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
const fmt = (n, d = 2) =>
  n == null || isNaN(n) ? "0" : Number(n).toLocaleString(undefined, { maximumFractionDigits: d });

export function OverviewBoard({ onOpenYard }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const today = todayStr();

  const refresh = useCallback(() => {
    setLoading(true);
    api
      .get("/overview", { params: { date: today } })
      .then((r) => setRows(r.data.projects || []))
      .catch(() => toast.error("Failed to load overview"))
      .finally(() => setLoading(false));
  }, [today]);

  useEffect(() => { refresh(); }, [refresh]);

  const totals = rows.reduce(
    (a, r) => ({
      panels: a.panels + r.panels,
      volume: a.volume + r.volume,
      loads: a.loads + r.loadsUsed,
      warnings: a.warnings + r.overCapacity + r.overlaps,
    }),
    { panels: 0, volume: 0, loads: 0, warnings: 0 }
  );

  return (
    <div className="bg-[#16181A] border border-[#2E3033]">
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#2E3033] gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <LayoutDashboard className="w-4 h-4 text-[#2560C9]" />
          <h2 className="font-heading font-bold text-white text-sm tracking-tight">YARDS OVERVIEW</h2>
          <span className="text-[10px] font-mono text-[#6E7787] ml-1">Today · {today}</span>
        </div>
        <button
          data-testid="overview-refresh"
          onClick={refresh}
          className="flex items-center gap-1.5 border border-[#2E3033] hover:bg-[#22252A] text-white text-xs px-2.5 py-1.5 transition-colors"
        >
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 border-b border-[#2E3033] divide-x divide-[#2E3033]">
        <Kpi label="Yards" value={rows.length} />
        <Kpi label="Panels (today)" value={totals.panels} />
        <Kpi label="Volume" value={`${fmt(totals.volume)} m³`} />
        <Kpi label="Loads Booked" value={totals.loads} color="#F59E0B" />
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-[#6E7787] text-xs font-mono p-6">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading overview…
        </div>
      ) : (
        <div data-testid="overview-grid" className="p-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {rows.map((r) => {
            const warnings = r.overCapacity + r.overlaps;
            return (
              <button
                key={r.id}
                data-testid={`overview-card-${r.id}`}
                onClick={() => onOpenYard(r.id)}
                className="group text-left bg-[#0A0A0A] border border-[#2E3033] hover:border-[#2560C9]/60 transition-colors p-4 flex flex-col"
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <Warehouse className="w-4 h-4 text-[#2560C9] shrink-0" />
                    <span className="font-heading font-bold text-white text-sm tracking-tight truncate">{r.name}</span>
                  </div>
                  <ArrowUpRight className="w-4 h-4 text-[#6E7787] group-hover:text-[#2560C9] transition-colors shrink-0" />
                </div>

                {!r.hasPlan && (
                  <span className="mt-1 text-[10px] font-mono uppercase tracking-wider text-[#6E7787]">No plan for today</span>
                )}

                <div className="mt-3 grid grid-cols-3 gap-2">
                  <Metric icon={Layers} label="Panels" value={r.panels} />
                  <Metric icon={MapPin} label="Volume" value={`${fmt(r.volume)} m³`} />
                  <Metric icon={Truck} label="Loads" value={r.loadsUsed} />
                </div>

                <div className="mt-3 flex items-center gap-1.5 flex-wrap">
                  <Pill color="#10B981">{r.onTable} on table</Pill>
                  <Pill color="#F59E0B">{r.inLoads} in loads</Pill>
                  <Pill color="#3B75DF">{r.unplaced} unplaced</Pill>
                </div>

                <div className="mt-3 pt-3 border-t border-[#2E3033]">
                  {warnings > 0 ? (
                    <div data-testid={`overview-warnings-${r.id}`} className="flex items-center gap-1.5 text-[11px] font-mono text-[#FF3B30]">
                      <AlertTriangle className="w-3.5 h-3.5" />
                      {r.overlaps > 0 && <span>{r.overlaps} overlap{r.overlaps !== 1 ? "s" : ""}</span>}
                      {r.overlaps > 0 && r.overCapacity > 0 && <span>·</span>}
                      {r.overCapacity > 0 && <span>{r.overCapacity} load{r.overCapacity !== 1 ? "s" : ""} over capacity</span>}
                    </div>
                  ) : (
                    <span className="text-[11px] font-mono text-[#10B981]">✓ No warnings</span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Kpi({ label, value, color }) {
  return (
    <div className="px-4 py-3">
      <p className="text-[9px] font-mono uppercase tracking-[0.2em] text-[#6E7787]">{label}</p>
      <p className="text-xl font-mono mt-1" style={{ color: color || "#ffffff" }}>{value}</p>
    </div>
  );
}

function Metric({ icon: Icon, label, value }) {
  return (
    <div className="bg-[#16181A] border border-[#2E3033] p-2">
      <span className="flex items-center gap-1 text-[9px] font-mono uppercase tracking-wider text-[#6E7787]">
        <Icon className="w-2.5 h-2.5" /> {label}
      </span>
      <p className="text-sm font-mono text-white mt-1 leading-none truncate">{value}</p>
    </div>
  );
}

function Pill({ color, children }) {
  return (
    <span className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 border" style={{ color, borderColor: `${color}55` }}>
      {children}
    </span>
  );
}
